import React, { useMemo } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Field, useFormState } from 'react-final-form';
import CheckToggleField from 'components/CheckToggleField';
import { CollapsibleSection } from 'components/CollapsibleSection';
import ShieldcheckIcon from 'icons/Shieldcheck.icon';
import DurationDaysField from 'components/DurationDaysField';
import WatermarkSection from './components/WatermarkSection';
import Tag from 'components/ListingPortfolioCard/components/Tag';
import { COLOR_VARIANT } from 'models/Colors';
import DefaultsSettingsTag from 'router/subrouters/DataRoom/components/DefaultsSettingsTag';

import './FileSecurity.styles.scss';

type FileSecurityProps = {
  className?: string;
  description?: string;
  defaultOpen?: boolean;
  showDefaultsSettingsSetup?: boolean;
  defaultsSettingsSetupAt?: Date;
  defaultsSettingsSetupBy?: { firstName: string; lastName?: string };
};

const FileSecurity: React.FC<FileSecurityProps> = (props) => {
  const {
    children,
    className,
    description,
    defaultsSettingsSetupAt,
    defaultsSettingsSetupBy,
    showDefaultsSettingsSetup = false,
    defaultOpen = true,
  } = props;

  const classes = classNames('file-security', 'white-panel', className);

  const { t } = useTranslation();

  const { values } = useFormState();

  const { expirationDays, downloadSecurePdf } = values || {};

  const expirationDatePlaceholder = useMemo(
    () => (!expirationDays ? t('mixed') : ''),
    [expirationDays, t],
  );

  const isDownloadSecureChecked = downloadSecurePdf?.checked;

  return (
    <CollapsibleSection
      className={classes}
      defaultOpen={defaultOpen}
      trigger={
        <div className="trigger__content">
          <div>
            <p className="name">{t('fileSecurity')}</p>

            <Tag
              content={t(isDownloadSecureChecked ? 'on' : 'off')}
              variant={
                COLOR_VARIANT[isDownloadSecureChecked ? 'GREEN' : 'ORANGE']
              }
            />

            {showDefaultsSettingsSetup && (
              <DefaultsSettingsTag
                defaultsSettingsSetupAt={defaultsSettingsSetupAt}
                defaultsSettingsSetupBy={defaultsSettingsSetupBy}
              />
            )}
          </div>

          {description && <p>{description}</p>}
        </div>
      }
    >
      <div className="file-security__settings">
        <Field
          name="downloadSecurePdf.checked"
          type="checkbox"
          label={t('addLayersProtectionToFile')}
          component={CheckToggleField}
        />

        {isDownloadSecureChecked ? (
          <>
            <div className="indent">
              <Field
                component={CheckToggleField}
                name="hasExpiration.checked"
                type="checkbox"
              />

              <DurationDaysField
                name="expirationDays"
                label={t('downloadsExpireAfter')}
                placeholder={expirationDatePlaceholder}
              >
                {t('ifDownloadabble')}
              </DurationDaysField>
            </div>

            <WatermarkSection />
          </>
        ) : (
          <div className="original">
            <ShieldcheckIcon />
            <span>{t('noteFilesDownloadedOriginal')}</span>
          </div>
        )}
      </div>

      {children}
    </CollapsibleSection>
  );
};

export default FileSecurity;
